import { MouseEvent, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import sprite from '../../../assets/icons/sprite.svg';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { CheckIndicator, CheckLabelSizes } from '../Check';
import { IconLabel } from '../IconLabel';
import { InputField } from '../InputField';

import classNames from 'classnames';
import classes from './Select.module.scss';

export interface SelectItem<T extends string | number | null = string | number> {
  id: T;
  title: string;
  hint?: string;
  logoUrl?: string;
  iconUrl?: string;
  disabled?: boolean;
}

export enum ListOpenDirection {
  top = 'top',
  bottom = 'bottom',
}

interface SelectProps<T extends string | number | null> {
  label?: string;
  placeholderLabel?: string;
  items: SelectItem<T>[];
  selectedItemId?: T;
  selectedItemIds?: T[];
  logoUrl?: string;
  iconUrl?: string;
  className?: string;
  listMinWidthRem?: number;
  listMaxHeightRem?: number;
  listOpenDirection?: ListOpenDirection;
  disabled?: boolean;
  active?: boolean;
  staticListPosition?: boolean;
  search?: boolean;
  uncheck?: boolean;
  noBorder?: boolean;
  noSidePadding?: boolean;
  compactSidePadding?: boolean;
  onChange?: (id?: T) => void;
  onMultiCheckChange?: (ids: T[]) => void;
}

interface RenderLogoArgs {
  url?: string;
  title?: string;
}

interface MultiCheckItemChangeArgs<T> {
  event: MouseEvent<HTMLButtonElement>;
  id: T;
}

interface SelectAllArgs {
  event: MouseEvent<HTMLButtonElement>;
  isChecked: boolean;
}

const genericMemo: <T>(component: T) => T = memo;

export const Select = genericMemo(
  <T extends string | number | null>({
    label,
    placeholderLabel,
    items,
    selectedItemId,
    selectedItemIds,
    logoUrl,
    iconUrl,
    className,
    listMinWidthRem,
    listMaxHeightRem,
    listOpenDirection = ListOpenDirection.bottom,
    disabled,
    active,
    staticListPosition,
    search,
    uncheck,
    noBorder,
    noSidePadding,
    compactSidePadding,
    onChange,
    onMultiCheckChange,
  }: SelectProps<T>) => {
    const { t } = useTranslation();

    const selectRef = useRef<HTMLDivElement>(null);

    const [isOpen, setIsOpen] = useState<boolean>(false);

    const [searchInput, setSearchInput] = useState<string>('');

    const [filteredItems, setFilteredItems] = useState<SelectItem<T>[] | undefined>();

    const headerRef = useRef<HTMLButtonElement>(null);

    const multiCheck = useMemo(() => Boolean(onMultiCheckChange), [onMultiCheckChange]);

    useOnClickOutside(selectRef, () => setIsOpen(false));

    useEffect(() => {
      if (!searchInput) {
        setFilteredItems(items);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items]);

    const onMultiCheckItemChange = useCallback(
      ({ event, id }: MultiCheckItemChangeArgs<T>) => {
        event.stopPropagation();

        const selectedItemIdsCopy = [...(selectedItemIds ?? [])];

        const index = selectedItemIdsCopy.findIndex((selectedId) => selectedId === id);

        index === -1 ? selectedItemIdsCopy.push(id) : selectedItemIdsCopy.splice(index, 1);

        onMultiCheckChange?.(selectedItemIdsCopy);
      },
      [onMultiCheckChange, selectedItemIds]
    );

    const renderIcon = useCallback((url?: string) => {
      if (!url) {
        return null;
      }

      return <div className={classes['icon']} style={{ backgroundImage: `url(${url})` }} />;
    }, []);

    const renderLogo = useCallback(({ url, title }: RenderLogoArgs) => {
      if (!url) {
        return null;
      }

      return <img className={classes['logo']} src={url} alt={title ?? ''} />;
    }, []);

    const headerContent = useMemo(() => {
      const title = !multiCheck
        ? items.find(({ id }) => id === selectedItemId)?.title
        : selectedItemIds?.length === items.length && items.length > 0
        ? t('common.all')
        : items
            .filter(({ id }) => selectedItemIds?.includes(id))
            .map(({ title }) => title)
            .join(', ');

      if (logoUrl) {
        return renderLogo({ url: logoUrl, title });
      }

      return (
        <>
          {label && <span className={classes['select__header-content-label']}>{label}</span>}
          <div className={classNames(classes['select__header-content-wrapper'])}>
            {renderIcon(iconUrl)}
            <span
              className={classNames(classes['select__header-content-title'], {
                [classes['select__header-content-title--without-label']]: !label,
              })}
            >
              {title || placeholderLabel || t('common.please-select')}
            </span>
          </div>
        </>
      );
    }, [
      iconUrl,
      items,
      label,
      logoUrl,
      multiCheck,
      placeholderLabel,
      renderIcon,
      renderLogo,
      selectedItemId,
      selectedItemIds,
      t,
    ]);

    const selectAll = useCallback(
      ({ event, isChecked }: SelectAllArgs) => {
        event.stopPropagation();

        onMultiCheckChange?.(isChecked ? [] : items.map(({ id }) => id));
      },
      [items, onMultiCheckChange]
    );

    const renderSelectAll = useMemo(() => {
      if (!multiCheck || !filteredItems || filteredItems.length < 2) {
        return null;
      }

      const isChecked = selectedItemIds?.length === items.length;

      const label = t(`common.${isChecked ? 'unselect' : 'select'}-all`);

      return (
        <li>
          <button
            type={'button'}
            className={classes['select__list-item']}
            aria-pressed={isChecked}
            onClick={(event) => selectAll({ event, isChecked })}
          >
            <CheckIndicator checked={isChecked} label={label} labelSize={CheckLabelSizes.large} />
          </button>
        </li>
      );
    }, [filteredItems, items.length, multiCheck, selectAll, selectedItemIds?.length, t]);

    const searchInputChange = useCallback(
      (value: string) => {
        setSearchInput(value);
        setFilteredItems(
          items.filter(({ title }) => title.toLowerCase().startsWith(value.toLowerCase()))
        );
      },
      [items]
    );

    const clearSearchInput = useCallback(() => {
      setSearchInput('');
      setFilteredItems(items);
    }, [items]);

    const renderSearch = useMemo(() => {
      if (!search) {
        return null;
      }

      return (
        <li className={classes['select__search']} onClick={(event) => event.stopPropagation()}>
          <InputField
            value={searchInput}
            onChange={({ target }) => searchInputChange(target.value)}
            placeholder={t('common.type-to-search')}
          />

          {searchInput && (
            <IconLabel
              className={classes['select__search-clear']}
              iconId={'close'}
              ariaLabel={t('common.clear')}
              singleColor
              onClick={clearSearchInput}
            />
          )}
        </li>
      );
    }, [clearSearchInput, search, searchInput, searchInputChange, t]);

    const renderMultiCheckListItem = useCallback(
      ({ id, title }: SelectItem<T>) => {
        const isSelected = Boolean(selectedItemIds?.includes(id));

        return (
          <li key={id ?? title}>
            <button
              type={'button'}
              className={classes['select__list-item']}
              aria-pressed={isSelected}
              onClick={(event) => onMultiCheckItemChange({ event, id })}
            >
              <CheckIndicator
                checked={isSelected}
                label={title}
                labelSize={CheckLabelSizes.large}
              />
            </button>
          </li>
        );
      },
      [onMultiCheckItemChange, selectedItemIds]
    );

    const onChangeHandler = useCallback(
      (id: T) => {
        onChange?.(uncheck && selectedItemId === id ? undefined : id);

        if (selectRef.current?.contains(document.activeElement)) {
          headerRef.current?.focus();
        }
      },
      [onChange, selectedItemId, uncheck]
    );

    const renderListItem = useCallback(
      (item: SelectItem<T>) => {
        if (multiCheck) {
          return renderMultiCheckListItem(item);
        }

        const { id, title, hint, logoUrl, iconUrl, disabled } = item;

        return (
          <li key={id ?? title}>
            <button
              type={'button'}
              className={classNames(classes['select__list-item'], {
                [classes['select__list-item--disabled']]: disabled,
              })}
              aria-disabled={disabled}
              {...(!disabled && { onClick: () => onChangeHandler(id) })}
            >
              {renderLogo({ url: logoUrl, title })}

              {!logoUrl && (
                <>
                  {renderIcon(iconUrl)}
                  <div className={classes['select__list-item-title']}>
                    {title}
                    {hint && <div className={classes['select__list-item-title-hint']}>{hint}</div>}
                  </div>
                </>
              )}

              {Boolean(id === selectedItemId) && (
                <svg className={classes['select__list-item-check']}>
                  <use href={`${sprite}#checkmark`} />
                </svg>
              )}
            </button>
          </li>
        );
      },
      [
        multiCheck,
        onChangeHandler,
        renderIcon,
        renderLogo,
        renderMultiCheckListItem,
        selectedItemId,
      ]
    );

    return (
      <div
        ref={selectRef}
        className={classNames(
          classes['select'],
          {
            [classes['select--disabled']]: disabled,
            [classes['select--active']]: active,
            [classes['select--no-border']]: noBorder,
            [classes['select--no-side-padding']]: noSidePadding,
            [classes['select--compact-side-padding']]: compactSidePadding,
          },
          className
        )}
        onClick={() => setIsOpen(!isOpen)}
      >
        <button
          ref={headerRef}
          type={'button'}
          className={classes['select__header']}
          aria-expanded={isOpen}
          disabled={disabled}
        >
          <div className={classes['select__header-content']}>{headerContent}</div>

          {!disabled && (
            <svg className={classes['select__header-arrow']}>
              <use href={`${sprite}#${isOpen ? 'up' : 'down'}-arrow`} />
            </svg>
          )}
        </button>

        {isOpen && (
          <ul className={classNames(classes['select__list-wrapper'])}>
            {renderSearch}
            <li>
              <ul
                className={classNames(
                  classes['select__list'],
                  classes[`select__list--open-${listOpenDirection}`],
                  { [classes['select__list--static-position']]: staticListPosition }
                )}
                style={{
                  ...(listMinWidthRem && { minWidth: `${listMinWidthRem}rem` }),
                  ...(listMaxHeightRem && { maxHeight: `${listMaxHeightRem}rem` }),
                }}
              >
                {renderSelectAll}
                {filteredItems?.map((item) => renderListItem(item))}
              </ul>
            </li>
          </ul>
        )}
      </div>
    );
  }
);
